import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { X, Building2, ChevronRight, ChevronLeft, Check, Lock, Loader2, Rocket, User, Mail, Phone, Globe, DollarSign, AlertCircle, RefreshCw, ShieldCheck } from "lucide-react";
import { PLANS, FEATURE_GROUPS, FEATURE_LABELS, type PlanFeatureKey } from "@/lib/plans";
import { CURRENCIES, COUNTRIES } from "@/lib/iso";
import { PhoneInput } from "@/components/PhoneInput";
import { apiPost } from "@/lib/api";
import { getGetMeQueryKey } from "@workspace/api-client-react";
import { useDemoProfile } from "@/context/DemoProfileContext";

const COMMON_CURRENCIES = ["USD", "EUR", "GBP", "AED", "SAR", "QAR", "KWD", "BHD", "OMR", "EGP", "INR", "SGD", "AUD", "CAD"];
const SORTED_CURRENCIES = [
  ...CURRENCIES.filter(c => COMMON_CURRENCIES.includes(c.code)),
  ...CURRENCIES.filter(c => !COMMON_CURRENCIES.includes(c.code)),
];

const TAX_LABELS = ["VAT", "GST", "HST", "Sales Tax", "Tax"];

type Step = "profile" | "plan" | "otp";
type EmailStatus = "idle" | "checking" | "available" | "active-demo" | "active-owner" | "expired";
type Company = { id: string; name: string; slug: string; plan: string; status: string; color?: string };

interface Props {
  open: boolean;
  initialPlanId?: string;
  onClose: () => void;
}

export function DemoSetupModal({ open, initialPlanId = "pro", onClose }: Props) {
  const [step, setStep] = useState<Step>("profile");
  const [planId, setPlanId] = useState(initialPlanId);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [emailStatus, setEmailStatus] = useState<EmailStatus>("idle");
  const [emailCheckRedirect, setEmailCheckRedirect] = useState<string | null>(null);
  const [country, setCountry] = useState("United Arab Emirates");
  const [phone, setPhone] = useState("");
  const [currency, setCurrency] = useState("AED");
  const [taxLabel, setTaxLabel] = useState("VAT");
  const [taxPct, setTaxPct] = useState("5");
  const [withDemoData, setWithDemoData] = useState(true);
  const [savedName, setSavedName] = useState("");
  const [savedEmail, setSavedEmail] = useState("");
  const [savedCompany, setSavedCompany] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [reentryMode, setReentryMode] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const companyRef = useRef<HTMLInputElement>(null);
  const [, setLocation] = useLocation();
  const qc = useQueryClient();
  const { setPlanId: savePlanOverride } = useDemoProfile();

  const checkEmail = useCallback(async (email: string) => {
    const trimmed = email.trim();
    if (!trimmed.includes("@")) return;
    setEmailStatus("checking");
    try {
      const res = await apiPost<{ status: EmailStatus; demoRedirect?: string }>("/auth/demo/check-email", { email: trimmed });
      setEmailStatus(res.status ?? "available");
      setEmailCheckRedirect(res.demoRedirect ?? null);
    } catch {
      setEmailStatus("available");
      setEmailCheckRedirect(null);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setStep("profile");
      setSubmitError("");
      setPlanId(initialPlanId);
      setSavedName("");
      setSavedEmail("");
      setSavedCompany("");
      setEmailStatus("idle");
      setEmailCheckRedirect(null);
      setWithDemoData(true);
      setOtpCode("");
      setOtpError("");
      setOtpSending(false);
      setOtpResendTimer(0);
      setReentryMode(false);
    }
  }, [open]);

  useEffect(() => {
    if (otpResendTimer <= 0) return;
    const t = setTimeout(() => setOtpResendTimer(v => v - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendTimer]);

  if (!open) return null;

  const activePlan = PLANS.find(p => p.id === planId) ?? PLANS[1];
  const isBlocked = emailStatus === "active-demo" || emailStatus === "active-owner" || emailStatus === "expired";

  const handleEmailBlur = () => {
    const val = emailRef.current?.value ?? "";
    if (val.trim().includes("@")) checkEmail(val);
    else setEmailStatus("idle");
  };

  const handleEmailChange = () => {
    if (emailStatus !== "idle") setEmailStatus("idle");
    if (submitError) setSubmitError("");
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    const name    = nameRef.current?.value.trim() ?? "";
    const email   = emailRef.current?.value.trim() ?? "";
    const company = companyRef.current?.value.trim() ?? "";
    if (!name)                { setSubmitError("Name is required"); return; }
    if (!email.includes("@")) { setSubmitError("Valid email is required"); return; }
    if (!company)             { setSubmitError("Company name is required"); return; }
    setSavedName(name);
    setSavedEmail(email);
    setSavedCompany(company);
    setSubmitError("");
    setStep("plan");
  };

  const formPayload = {
    email: savedEmail, name: savedName, company: savedCompany,
    country, phone, currency, taxLabel, taxPct, planId, seedData: withDemoData,
  };

  const sendOtp = async () => {
    setOtpSending(true);
    setOtpError("");
    try {
      if (reentryMode) {
        await apiPost("/auth/demo/send-reentry-otp", { email: savedEmail });
      } else {
        await apiPost("/auth/demo/send-otp", formPayload);
      }
      setOtpResendTimer(60);
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
      setOtpError(data?.error ?? "Failed to send verification code.");
    } finally {
      setOtpSending(false);
    }
  };

  const handleReentryAccess = async () => {
    const email = emailRef.current?.value.trim() ?? "";
    if (!email) return;
    setLoading(true);
    setSubmitError("");
    setSavedEmail(email);
    setReentryMode(true);
    try {
      await apiPost("/auth/demo/send-reentry-otp", { email });
      setOtpResendTimer(60);
      setOtpCode("");
      setOtpError("");
      setStep("otp");
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
      setSubmitError(data?.error ?? "Failed to send verification email.");
    } finally {
      setLoading(false);
    }
  };

  const handleLaunch = async () => {
    setLoading(true);
    setSubmitError("");
    try {
      await apiPost("/auth/demo/send-otp", formPayload);
      setOtpResendTimer(60);
      setOtpCode("");
      setOtpError("");
      setStep("otp");
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
      setSubmitError(data?.error ?? "Failed to send verification email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) { setOtpError("Please enter the 6-digit code."); return; }
    setLoading(true);
    setOtpError("");
    try {
      if (reentryMode) {
        const res = await apiPost<{ success: boolean; redirect?: string }>("/auth/demo/reenter", { email: savedEmail, code: otpCode });
        await qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
        if (res.redirect) {
          setLocation(res.redirect);
          onClose();
          return;
        }
        setOtpError("Something went wrong. Please try again.");
      } else {
        await apiPost<{ success: boolean }>("/auth/demo/verify-otp", { email: savedEmail, code: otpCode });
        const res = await apiPost<{ user?: object; companies?: Company[]; redirect?: string | null }>(
          "/auth/demo/instant", formPayload
        );
        await qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
        if (res.redirect) {
          savePlanOverride(planId, res.companies?.[0]?.id);
          setLocation(res.redirect);
          onClose();
          return;
        }
        if (res.companies && res.companies.length > 0) {
          const best = res.companies[0];
          const pickRes = await apiPost<{ redirect?: string }>("/auth/select-company", { companyId: best.id });
          await qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
          if (pickRes.redirect) {
            savePlanOverride(planId, best.id);
            setLocation(pickRes.redirect);
            onClose();
            return;
          }
        }
        setOtpError("Something went wrong. Please try again.");
      }
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
      setOtpError(data?.error ?? "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldDisabled = isBlocked;
  const inputCls = (extra = "") =>
    `w-full px-3 py-2.5 border rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${extra} ${fieldDisabled ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed opacity-60" : "border-slate-200"}`;
  const selectCls = (extra = "") =>
    `w-full px-3 py-2.5 border rounded-xl text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${extra} ${fieldDisabled ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed opacity-60" : "border-slate-200"}`;

  /* ── Status banner rendered below the name/email row ── */
  const StatusBanner = () => {
    if (emailStatus === "checking") {
      return (
        <div className="flex items-center gap-2 text-xs text-slate-500 px-1">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
          Checking email…
        </div>
      );
    }
    if (emailStatus === "available") {
      return (
        <div className="flex items-center gap-2 text-xs text-emerald-600 px-1">
          <Check className="w-3.5 h-3.5" />
          Email is available — fill in the details below
        </div>
      );
    }
    if (emailStatus === "active-demo") {
      return (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 space-y-2">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800 font-medium">You already have an active demo workspace.</p>
          </div>
          <p className="text-xs text-blue-700">Verify your email to access it.</p>
          <button
            type="button"
            disabled={loading}
            onClick={handleReentryAccess}
            className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-white px-3 py-2.5 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #1e40af 0%, #0d4a4a 100%)" }}
          >
            {loading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending code…</>
              : <><Mail className="w-4 h-4" /> Verify &amp; Access Workspace</>
            }
          </button>
        </div>
      );
    }
    if (emailStatus === "active-owner") {
      return (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 space-y-2">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900 font-medium">This email has an active subscription account.</p>
          </div>
          <button
            type="button"
            onClick={() => { setLocation("/login"); onClose(); }}
            className="w-full text-sm font-semibold text-white px-3 py-2.5 rounded-lg hover:opacity-90 transition-opacity"
            style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}
          >
            Sign in to your account →
          </button>
        </div>
      );
    }
    if (emailStatus === "expired") {
      return (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 space-y-2">
          <div className="flex items-start gap-2">
            <RefreshCw className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
            <p className="text-sm text-rose-800 font-medium">Your previous access has expired. Sign up for a plan to continue.</p>
          </div>
          <button
            type="button"
            onClick={() => { setLocation("/signup"); onClose(); }}
            className="w-full text-sm font-semibold text-white px-3 py-2.5 rounded-lg hover:opacity-90 transition-opacity"
            style={{ background: "linear-gradient(135deg, #be123c 0%, #1a3a5c 100%)" }}
          >
            Sign up to access the system →
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={{ background: "rgba(5,15,30,0.80)", backdropFilter: "blur(6px)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col" style={{ maxWidth: 620, maxHeight: "90vh" }}>
        <div className="h-1.5 w-full shrink-0" style={{ background: "linear-gradient(90deg, #0d4a4a 0%, #1a3a5c 100%)" }} />

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 transition-colors p-1 rounded-lg hover:bg-slate-100 z-10"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-7 pt-7 pb-3 shrink-0">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md shrink-0" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}>
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Set up your demo workspace</h2>
              <p className="text-sm text-slate-500">Personalised access · Email verified · No credit card</p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-1">
            {(["profile", "plan", "otp"] as Step[]).map((s, i) => {
              const LABELS: Record<Step, string> = { profile: "Your company", plan: "Choose plan", otp: "Verify email" };
              const ORDER: Step[] = ["profile", "plan", "otp"];
              const currentIdx = ORDER.indexOf(step);
              const stepIdx = ORDER.indexOf(s);
              const isActive = step === s;
              const isDone = stepIdx < currentIdx;
              return (
                <div key={s} className="flex items-center gap-2">
                  {i > 0 && <div className="w-6 h-px bg-slate-200" />}
                  <div className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                    isActive ? "text-white" : isDone ? "text-emerald-700 bg-emerald-50" : "text-slate-400 bg-slate-100"
                  }`} style={isActive ? { background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" } : {}}>
                    {isDone ? <Check className="w-3 h-3" /> : <span>{i + 1}</span>}
                    <span>{LABELS[s]}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-7 pb-6">
          {step === "profile" && (
            <form id="profile-form" onSubmit={handleNext} className="space-y-4">

              {/* Name + Email row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    <User className="w-3 h-3 inline mr-1" />Full Name *
                  </label>
                  <input
                    ref={nameRef}
                    type="text"
                    required
                    placeholder="Ahmed Al-Rashid"
                    className={inputCls()}
                    autoFocus
                    disabled={fieldDisabled}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    <Mail className="w-3 h-3 inline mr-1" />Work Email *
                  </label>
                  <input
                    ref={emailRef}
                    type="email"
                    required
                    placeholder="you@company.com"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onBlur={handleEmailBlur}
                    onChange={handleEmailChange}
                  />
                </div>
              </div>

              {/* Email status banner — shown between email row and company row */}
              <StatusBanner />

              {/* Remaining fields — disabled when email is blocked */}
              <div className={isBlocked ? "opacity-50 pointer-events-none select-none" : ""}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                      <Building2 className="w-3 h-3 inline mr-1" />Company Name *
                    </label>
                    <input
                      ref={companyRef}
                      type="text"
                      required={!isBlocked}
                      placeholder="Al Rashid Contracting LLC"
                      className={inputCls()}
                      disabled={fieldDisabled}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                        <Globe className="w-3 h-3 inline mr-1" />Country
                      </label>
                      <select
                        value={country}
                        onChange={e => setCountry(e.target.value)}
                        className={selectCls()}
                        disabled={fieldDisabled}
                      >
                        {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                        <Phone className="w-3 h-3 inline mr-1" />Phone <span className="text-slate-400 font-normal normal-case">(optional)</span>
                      </label>
                      <PhoneInput name="_phone_demo_setup" country={country} onChange={setPhone} />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                        <DollarSign className="w-3 h-3 inline mr-1" />Currency
                      </label>
                      <select
                        value={currency}
                        onChange={e => setCurrency(e.target.value)}
                        className={selectCls()}
                        disabled={fieldDisabled}
                      >
                        {SORTED_CURRENCIES.map(c => (
                          <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Tax Label</label>
                      <select
                        value={taxLabel}
                        onChange={e => setTaxLabel(e.target.value)}
                        className={selectCls()}
                        disabled={fieldDisabled}
                      >
                        {TAX_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Default {taxLabel} %</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={taxPct}
                        onChange={e => setTaxPct(e.target.value)}
                        className={inputCls()}
                        disabled={fieldDisabled}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Start with sample data</p>
                      <p className="text-xs text-slate-500 mt-0.5">Pre-filled with example projects, invoices &amp; BOQ</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => !fieldDisabled && setWithDemoData(v => !v)}
                      className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${withDemoData ? "" : "bg-slate-200"}`}
                      style={withDemoData ? { background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" } : {}}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${withDemoData ? "translate-x-5" : "translate-x-0"}`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Submission error (only from submit attempt, not email check) */}
              {submitError && (
                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                  {submitError}
                </div>
              )}

              {/* Submit button — hidden when blocked */}
              {!isBlocked && (
                <button
                  type="submit"
                  disabled={emailStatus === "checking"}
                  className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity shadow-md text-sm mt-2 disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}
                >
                  {emailStatus === "checking"
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</>
                    : <>Choose your plan <ChevronRight className="w-4 h-4" /></>
                  }
                </button>
              )}
            </form>
          )}
          {step === "plan" && (
            <div className="space-y-4">
              <div className="flex gap-2">
                {PLANS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setPlanId(p.id)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${
                      planId === p.id
                        ? "text-slate-900 border-transparent"
                        : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                    style={planId === p.id ? { background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)", color: "white", borderColor: "transparent" } : {}}
                  >
                    {p.label}
                    {p.popular && planId !== p.id && (
                      <span className="ml-1.5 text-[10px] text-blue-600 font-bold bg-blue-50 px-1.5 py-0.5 rounded-full">Popular</span>
                    )}
                  </button>
                ))}
              </div>

              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-lg font-bold text-slate-900">{activePlan.price}</span>
                  <span className="text-xs text-slate-500">{activePlan.maxProjects === -1 ? "Unlimited" : `Up to ${activePlan.maxProjects}`} projects · {activePlan.maxUsers === -1 ? "Unlimited" : `Up to ${activePlan.maxUsers}`} users</span>
                </div>
                <p className="text-sm text-slate-500 mb-4">{activePlan.description}</p>

                <div className="space-y-4">
                  {FEATURE_GROUPS.map(group => (
                    <div key={group.label}>
                      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">{group.label}</p>
                      <div className="grid grid-cols-1 gap-1">
                        {group.keys.map((key: PlanFeatureKey) => {
                          const included = activePlan.features[key];
                          return (
                            <div key={key} className={`flex items-center gap-2 py-1 ${included ? "" : "opacity-40"}`}>
                              {included
                                ? <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                : <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                              }
                              <span className={`text-xs ${included ? "text-slate-700" : "text-slate-400 line-through"}`}>
                                {FEATURE_LABELS[key]}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {submitError && (
                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                  {submitError}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setStep("profile")}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={handleLaunch}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 text-white font-semibold py-2.5 rounded-xl hover:opacity-90 transition-opacity shadow-md text-sm disabled:opacity-70"
                  style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}
                >
                  {loading
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending code…</>
                    : <><Mail className="w-4 h-4" /> Verify Email &amp; Launch</>
                  }
                </button>
              </div>

              <p className="text-center text-xs text-slate-400">
                We'll send a 6-digit code to your email to verify your identity.
              </p>
            </div>
          )}
          {step === "otp" && (
            <div className="space-y-6 py-2">
              <div className="text-center space-y-2">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto shadow-md" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}>
                  <ShieldCheck className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Check your inbox</h3>
                <p className="text-sm text-slate-500">
                  We sent a 6-digit verification code to<br />
                  <span className="font-semibold text-slate-700">{savedEmail}</span>
                </p>
              </div>

              <div className="flex justify-center">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={otpCode}
                  onChange={e => { setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setOtpError(""); }}
                  placeholder="000000"
                  className="w-48 text-center text-3xl font-bold tracking-[0.5em] border-2 border-slate-200 rounded-xl py-3 text-slate-900 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  autoFocus
                />
              </div>

              {otpError && (
                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-center">
                  {otpError}
                </div>
              )}

              <button
                onClick={handleVerifyOtp}
                disabled={loading || otpCode.length !== 6}
                className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity shadow-md text-sm disabled:opacity-70"
                style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}
              >
                {loading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating workspace…</>
                  : <><Rocket className="w-4 h-4" /> Verify &amp; Launch Demo</>
                }
              </button>

              <div className="text-center space-y-1">
                <p className="text-xs text-slate-400">
                  Didn't receive the code? Check your spam folder.
                </p>
                {otpResendTimer > 0 ? (
                  <p className="text-xs text-slate-400">Resend available in {otpResendTimer}s</p>
                ) : (
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={otpSending}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  >
                    {otpSending ? "Sending…" : "Resend code"}
                  </button>
                )}
              </div>

              {!reentryMode && (
                <button
                  onClick={() => setStep("plan")}
                  className="w-full flex items-center justify-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Back to plan selection
                </button>
              )}
              {reentryMode && (
                <button
                  onClick={() => { setReentryMode(false); setStep("profile"); }}
                  className="w-full flex items-center justify-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
